import { useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

const SIZE_PX: Record<'xs' | 'sm', number> = {
  xs: 14,
  sm: 18,
};

export interface SiteFaviconProps {
  /** The WordPress site's "Site Icon" URL (`channel.siteIconUrl`) — public http(s), or absent/null when unset. */
  iconUrl?: string | null;
  /** Site name/host, used for the image's `alt` text. */
  siteName?: string;
  size?: 'xs' | 'sm';
  className?: string;
}

/**
 * Shows a connected WordPress site's favicon (its "Site Icon"), falling back
 * to the existing globe icon when the site has none set or the image fails
 * to load (e.g. a stale/404 favicon URL). Deliberately does NOT use
 * `RetryImage` — a missing favicon should fall back to the globe immediately
 * rather than retry-looping a third-party asset.
 */
export const SiteFavicon = ({ iconUrl, siteName, size = 'xs', className }: SiteFaviconProps) => {
  const [errored, setErrored] = useState(false);

  if (iconUrl && !errored) {
    const px = SIZE_PX[size];
    return (
      <img
        src={iconUrl}
        alt={siteName ? `${siteName} icon` : ''}
        width={px}
        height={px}
        style={{ width: px, height: px }}
        className={cn('rounded-sm object-cover flex-shrink-0', className)}
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setErrored(true)}
      />
    );
  }

  return <Icon name={ICONS.GLOBE} size={size} ariaHidden className={className} />;
};
